import { Platform } from 'react-native';

// Load expo-notifications LAZILY and defensively. If the current native build
// doesn't include the module yet, requiring it throws — we swallow that so the
// app keeps running, and notifications simply activate after the next rebuild.
let Notifications: any = null;
try {
  Notifications = require('expo-notifications');
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: true,
      shouldSetBadge: false,
    }),
  });
} catch {
  Notifications = null;
}

// ── Schedule times (local device time) ──────────────────────────────────────
const STREAK_HOUR = 19;      // Pro daily streak reminder (7:15 PM)
const STREAK_MINUTE = 15;
const LASTCALL_HOUR = 21;    // Duolingo-style "streak expires tonight" escalation (9:30 PM)
const LASTCALL_MINUTE = 30;
const EARLYBIRD_HOUR = 8;    // Free daily early-bird nudge
const EARLYBIRD_MINUTE = 0;
const SCAN_READY_HOUR = 10;  // time of day the "scan ready" alert fires
const FREE_SCAN_DAYS = 7;    // free tier: one scan per 7 days
const SCORE_TEASE_DELAY_SEC = 2 * 60 * 60; // 2h after a free scan

// ── Copy banks ──────────────────────────────────────────────────────────────
// Pro: real streak/habit reminders (they have the program + tasks).
const STREAK_REMINDERS = [
  { title: "Don't break your streak 🔥", body: 'Knock out today’s tasks and keep the glow-up going.' },
  { title: 'Your streak is waiting 🔥', body: 'Two minutes of tasks keeps the momentum alive.' },
  { title: 'Lock in 🔒', body: 'Finish today’s habits before the day ends.' },
];

// Free: early-bird motivation to start the day + pull them back in.
const EARLYBIRD_REMINDERS = [
  { title: 'Morning. Time to lock in 🔒', body: 'Open Maxify and check where your looks stand.' },
  { title: 'Early bird glow-up 🌅', body: 'The guys winning start their day here. Tap in.' },
  { title: 'New day, new gains 💪', body: 'See your scores and what to work on today.' },
];

// Free: FOMO + re-engagement teasing the paid program.
const FOMO_REMINDERS = [
  { title: 'Pro guys are leveling up 📈', body: 'They’re on their 8-week glow-up plan. Start yours.' },
  { title: 'Your full breakdown is locked 🔒', body: 'Unlock all 6 scores and see exactly what to fix.' },
  { title: 'Still curious about your potential? 👀', body: 'Pro unlocks your personalized plan + every score.' },
  { title: 'A week of progress missed', body: 'Pro members tracked theirs. Catch up — upgrade today.' },
];

const SCAN_READY = {
  title: 'Your weekly scan is ready 👀',
  body: 'Have you improved? Rescan and watch your score climb.',
};

const SCORE_TEASE = {
  title: 'Your full breakdown is waiting 🔒',
  body: 'Jawline, symmetry, skin and more — unlock every score with Pro.',
};

// ── Helpers ─────────────────────────────────────────────────────────────────
function pickDaily<T>(arr: T[]): T {
  return arr[new Date().getDate() % arr.length];
}

// Duolingo-style flame copy — personalized with the REAL streak count instead
// of a generic nag. `dayOffset` 0 = tonight; 1+ = future evenings, where the
// shown count assumes the streak keeps going (the schedule is refreshed on
// every launch and every task completion, so a stale count only survives if
// the app is never opened — same tradeoff Duolingo makes).
function flameCopy(streak: number, dayOffset: number): { title: string; body: string } {
  if (streak <= 0) return pickDaily(STREAK_REMINDERS);
  const n = streak + dayOffset;
  const variants = [
    { title: `Your ${n}-day streak is on the line 🔥`, body: 'One task keeps it alive. Two minutes, tops.' },
    { title: `${n} days strong. Keep it that way 🔥`, body: "Don't let tonight be the night the flame dies." },
    { title: `🔥 ${n}-day streak check`, body: 'One quick task before midnight = streak safe.' },
  ];
  return variants[(new Date().getDate() + dayOffset) % variants.length];
}

/** Ask for permission (once). Returns whether granted + whether it was just granted now. */
async function ensurePermission(): Promise<{ granted: boolean; firstTime: boolean }> {
  const current = await Notifications.getPermissionsAsync();
  if (current.granted) return { granted: true, firstTime: false };
  const req = await Notifications.requestPermissionsAsync();
  return { granted: req.granted, firstTime: req.granted };
}

async function ensureAndroidChannel(): Promise<void> {
  if (Platform.OS !== 'android') return;
  await Notifications.setNotificationChannelAsync('streak', {
    name: 'Reminders',
    importance: Notifications.AndroidImportance.HIGH,
  });
}

function dailyTrigger(hour: number, minute: number) {
  return { type: Notifications.SchedulableTriggerInputTypes.DAILY, hour, minute };
}

/**
 * Schedule the right set of reminders based on subscription state. Safe to call
 * on every launch and whenever sub state / last-scan changes — it cancels the
 * old schedule first so reminders never stack. No-ops if the native module
 * isn't available or permission is denied.
 *
 * Pro users  → Duolingo-style flame reminders: one-shot evening pings carrying
 *              the user's REAL streak count, silenced for today once the streak
 *              is safe, plus a 9:30 PM "expires tonight" last call when a live
 *              streak is still at risk.
 * Free users → 8 AM early-bird daily nudge, a "scan ready" alert when their
 *              7-day window reopens, and a couple of FOMO/re-engagement pings.
 */
export async function scheduleReminders(opts: {
  isSubscribed: boolean;
  lastScanDate?: string | null;
  /** Current streak count — personalizes the flame copy. */
  streakDays?: number;
  /** True once ≥1 task is done today — silences TODAY's flame pings (the streak is safe). */
  streakSafeToday?: boolean;
}): Promise<void> {
  if (!Notifications) return;
  try {
    const { granted, firstTime } = await ensurePermission();
    if (!granted) return;
    await ensureAndroidChannel();
    await Notifications.cancelAllScheduledNotificationsAsync();

    if (opts.isSubscribed) {
      const streak = opts.streakDays ?? 0;

      // Flame reminders: a 4-evening runway of one-shots at 7:15 PM. One-shots
      // (not a DAILY trigger) so that finishing today's tasks actually silences
      // tonight's ping — every launch and every completion reschedules, which
      // keeps the runway rolling and the copy's count current.
      for (let day = 0; day < 4; day++) {
        if (day === 0 && opts.streakSafeToday) continue; // today already safe — stay quiet
        const at = atHourDaysFromNow(day, STREAK_HOUR, STREAK_MINUTE);
        if (at.getTime() <= Date.now()) continue;
        const c = flameCopy(streak, day);
        await Notifications.scheduleNotificationAsync({
          content: { title: c.title, body: c.body, sound: true },
          trigger: { type: Notifications.SchedulableTriggerInputTypes.DATE, date: at },
        });
      }

      // Last call — only when a real streak is on the line tonight. The urgency
      // beat is what makes the flame feel alive; never fires once they're done.
      if (!opts.streakSafeToday && streak > 0) {
        const lastCall = atHourDaysFromNow(0, LASTCALL_HOUR, LASTCALL_MINUTE);
        if (lastCall.getTime() > Date.now()) {
          await Notifications.scheduleNotificationAsync({
            content: {
              title: `⏰ Your ${streak}-day streak expires tonight`,
              body: 'One task before midnight keeps the flame alive. 30 seconds. Go.',
              sound: true,
            },
            trigger: { type: Notifications.SchedulableTriggerInputTypes.DATE, date: lastCall },
          });
        }
      }

      // Weekly rescan nudge — the progress-check loop is a Pro perk now that
      // free users get a single lifetime scan.
      const scanReadyAt = nextScanReadyDate(opts.lastScanDate);
      if (scanReadyAt) {
        await Notifications.scheduleNotificationAsync({
          content: { title: SCAN_READY.title, body: SCAN_READY.body, sound: true },
          trigger: { type: Notifications.SchedulableTriggerInputTypes.DATE, date: scanReadyAt },
        });
      }
    } else {
      // Free: early-bird daily nudge. (No "scan ready" alert — the free tier
      // has one lifetime scan, so that ping would be a lie.)
      const eb = pickDaily(EARLYBIRD_REMINDERS);
      await Notifications.scheduleNotificationAsync({
        content: { title: eb.title, body: eb.body, sound: true },
        trigger: dailyTrigger(EARLYBIRD_HOUR, EARLYBIRD_MINUTE),
      });

      // FOMO / re-engagement pings on day 2 and day 4 (10 AM each).
      for (const dayOffset of [2, 4]) {
        const at = atHourDaysFromNow(dayOffset, SCAN_READY_HOUR);
        const f = FOMO_REMINDERS[dayOffset % FOMO_REMINDERS.length];
        await Notifications.scheduleNotificationAsync({
          content: { title: f.title, body: f.body, sound: true },
          trigger: { type: Notifications.SchedulableTriggerInputTypes.DATE, date: at },
        });
      }
    }

    // Sunday recap — every Sunday evening; the in-app recap card carries the
    // real numbers, this just brings them back to see it.
    await Notifications.scheduleNotificationAsync({
      content: {
        title: 'Your week in review 📊',
        body: 'Tasks, streak, and score moves — open your Sunday recap.',
        sound: true,
      },
      trigger: { type: Notifications.SchedulableTriggerInputTypes.WEEKLY, weekday: 1, hour: 18, minute: 0 },
    });

    if (firstTime) {
      await Notifications.scheduleNotificationAsync({
        content: {
          title: 'Notifications on 🔥',
          body: "You're set — we'll keep you on track with your glow-up.",
          sound: true,
        },
        trigger: { type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL, seconds: 4 },
      });
    }
  } catch (e) {
    console.error('Notification scheduling failed:', e);
  }
}

/**
 * Tease the locked score breakdown ~2 hours after a FREE user finishes a scan.
 * Call this right AFTER scheduleReminders so it isn't cancelled. No-op for Pro.
 */
export async function scheduleScoreTease(): Promise<void> {
  if (!Notifications) return;
  try {
    const current = await Notifications.getPermissionsAsync();
    if (!current.granted) return;
    await Notifications.scheduleNotificationAsync({
      content: { title: SCORE_TEASE.title, body: SCORE_TEASE.body, sound: true },
      trigger: {
        type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL,
        seconds: SCORE_TEASE_DELAY_SEC,
      },
    });
  } catch (e) {
    console.error('Score tease scheduling failed:', e);
  }
}

/** Turn off all scheduled reminders (used if the user opts out). */
export async function cancelStreakReminders(): Promise<void> {
  if (!Notifications) return;
  try {
    await Notifications.cancelAllScheduledNotificationsAsync();
  } catch (e) {
    console.error('Failed to cancel notifications:', e);
  }
}

// ── Date math ───────────────────────────────────────────────────────────────
/** The next moment the free weekly scan reopens, or null if it's already open. */
function nextScanReadyDate(lastScanDate?: string | null): Date | null {
  if (!lastScanDate) return null; // never scanned — nothing to wait for
  const last = new Date(`${lastScanDate}T00:00:00`).getTime();
  if (Number.isNaN(last)) return null;
  const ready = new Date(last + FREE_SCAN_DAYS * 86400000);
  ready.setHours(SCAN_READY_HOUR, 0, 0, 0);
  return ready.getTime() > Date.now() ? ready : null;
}

function atHourDaysFromNow(days: number, hour: number, minute = 0): Date {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, minute, 0, 0);
  return d;
}
